import { useEffect, useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, ArrowLeft, Wrench, IndianRupee, Clock, X, Search } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Services() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // service | "new" | null

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/services");
      setRows(data.services || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load services");
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) => r.name.toLowerCase().includes(s) || (r.description || "").toLowerCase().includes(s));
  }, [rows, q]);

  const activeCount = useMemo(() => (rows || []).filter((r) => r.active).length, [rows]);

  const remove = async (svc) => {
    const useMsg = svc.assigned_staff_count > 0
      ? `Delete "${svc.name}"?\n\nThis will remove it from ${svc.assigned_staff_count} team member${svc.assigned_staff_count === 1 ? "" : "s"}. Existing appointments will keep the historical service name.`
      : `Delete "${svc.name}"?`;
    if (!confirm(useMsg)) return;
    try {
      const { data } = await api.delete(`/services/${svc.service_id}`);
      toast.success(
        `Service "${data.service_name}" deleted.`,
        data.detached_staff_count > 0
          ? { description: `Removed from ${data.detached_staff_count} team member${data.detached_staff_count === 1 ? "" : "s"}.` }
          : undefined,
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleActive = async (svc) => {
    try {
      await api.patch(`/services/${svc.service_id}`, { active: !svc.active });
      toast.success(`"${svc.name}" ${!svc.active ? "activated" : "deactivated"}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F2]">
      <Sidebar />
      <main className="nexa-compact lg:ml-[260px] min-h-screen">
        <div className="w-full max-w-[1080px] mx-auto px-4 md:px-8 py-6 md:py-10">
          <button
            onClick={() => nav("/business-settings")}
            className="flex items-center gap-1.5 text-[10.5px] text-[#102C2D] hover:text-[#075B59] mb-4 transition-colors"
            data-testid="svc-back-btn"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={1.6} /> Business Settings
          </button>

          <motion.header
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6"
          >
            <div>
              <h1 className="mt-1 text-[18px] md:text-[19px] font-semibold tracking-[-0.02em] text-nexa-ink">
                Your <span className="italic">service catalog</span>
              </h1>
              <p className="mt-1 text-[11px] text-nexa-mute max-w-[54ch]">
                Manage the services you offer. Only active services appear on the appointment form and staff picker.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="lux-card px-3 py-2 text-[10.5px] text-nexa-mute">
                <span className="text-nexa-ink font-semibold">{activeCount}</span> active
                {rows && <span className="text-nexa-mute"> · {rows.length} total</span>}
              </div>
              <button
                onClick={() => setEditing("new")}
                className="lux-btn-accent !rounded-full !bg-[#075B59] !border-[#075B59] !text-white !font-semibold hover:!bg-[#064B49] hover:!border-[#064B49]"
                data-testid="svc-add-btn"
              >
                <Plus className="!mr-0 !h-4 !w-4" strokeWidth={2} />
                Add service
              </button>
            </div>
          </motion.header>

          {/* Search */}
          {rows && rows.length > 3 && (
            <div className="relative mb-4">
              <Search className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 text-nexa-mute" strokeWidth={1.6} />
              <input
                data-testid="svc-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search services…"
                className="lux-input !border-[#E0E8E3] !bg-white !pl-8 focus:!border-[#75BDB0] focus:!ring-2 focus:!ring-[#D9EDE5]"
              />
            </div>
          )}

          <section className="space-y-1.5">
            {rows === null && [1, 2, 3].map((i) => (
              <div key={i} className="lux-card p-3.5">
                <div className="lux-skel h-3 w-1/3 mb-2" />
                <div className="lux-skel h-2.5 w-2/3" />
              </div>
            ))}

            {rows && rows.length === 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="lux-card p-10 text-center" data-testid="svc-empty">
                <div className="mx-auto mb-4 w-9 h-9 rounded-full bg-nexa-canvas flex items-center justify-center">
                  <Wrench className="w-4 h-4 text-nexa-mute" strokeWidth={1.5} />
                </div>
                <h3 className="text-[13px] font-semibold text-nexa-ink">No services yet.</h3>
                <p className="mt-1.5 text-[11px] text-nexa-mute max-w-[42ch] mx-auto">
                  Add the services you offer so staff and appointments can reference them.
                </p>
                <button onClick={() => setEditing("new")} className="lux-btn-accent mt-5" data-testid="svc-empty-cta">
                  <Plus className="w-3 h-3 mr-1" strokeWidth={2} /> Add service
                </button>
              </motion.div>
            )}

            {filtered && filtered.map((s) => (
              <ServiceRow key={s.service_id} s={s} onEdit={() => setEditing(s)} onDelete={() => remove(s)} onToggle={() => toggleActive(s)} />
            ))}
          </section>
        </div>
      </main>

      <AnimatePresence>
        {editing && (
          <ServiceModal
            service={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ServiceRow({ s, onEdit, onDelete, onToggle }) {
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className="lux-card !border-[#E0E8E3] !bg-white px-3 py-2.5 md:px-3.5 md:py-3 lux-card-hover transition-all duration-300 hover:!border-[#B8DCD0] hover:!shadow-[0_10px_25px_rgba(7,91,89,0.08)]"
      data-testid={`svc-row-${s.service_id}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#E8F4EF] border border-[#D9EDE5] flex items-center justify-center shrink-0">
          <Wrench className="w-3.5 h-3.5 text-[#075B59]" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <div className="text-[13px] font-semibold text-nexa-ink truncate">{s.name}</div>
            {s.active
              ? <span className="lux-chip lux-chip-success !border-[#C7E7D7] !bg-[#E8F4EF] !text-[#087F63] !text-[8.5px]" data-testid={`svc-status-${s.service_id}`}>Active</span>
              : <span className="lux-chip !text-[8.5px]" data-testid={`svc-status-${s.service_id}`}>Inactive</span>}
            {s.assigned_staff_count > 0 && (
              <span className="lux-chip !text-[8.5px]">
                {s.assigned_staff_count} staff
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-nexa-mute">
            <span className="inline-flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" strokeWidth={1.6} />
              {s.duration_min} min
            </span>
            <span className="inline-flex items-center gap-1">
              <IndianRupee className="w-2.5 h-2.5" strokeWidth={1.6} />
              ₹{Number(s.amount || 0).toLocaleString()}
            </span>
          </div>
          {s.description && (
            <p className="mt-0.5 text-[9.5px] text-nexa-mute line-clamp-1 max-w-[60ch]">{s.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onToggle}
            className="lux-icon-btn !w-6 !h-6"
            aria-label={s.active ? "Deactivate" : "Activate"}
            data-testid={`svc-toggle-${s.service_id}`}
            title={s.active ? "Deactivate" : "Activate"}
          >
            <span className={`w-2 h-2 rounded-full ${s.active ? "bg-emerald-500" : "bg-nexa-line"}`} />
          </button>
          <button onClick={onEdit} className="lux-icon-btn !w-6 !h-6" aria-label="Edit" data-testid={`svc-edit-${s.service_id}`}>
            <Pencil className="w-3 h-3" strokeWidth={1.6} />
          </button>
          <button onClick={onDelete} className="lux-icon-btn !w-7 !h-7 hover:!text-red-500" aria-label="Delete" data-testid={`svc-delete-${s.service_id}`}>
            <Trash2 className="w-3 h-3" strokeWidth={1.6} />
          </button>
        </div>
      </div>
    </motion.article>
  );
}

function ServiceModal({ service, onClose, onSaved }) {
  const isEdit = !!service;
  const [form, setForm] = useState({
    name: service?.name || "",
    duration_min: service?.duration_min || 30,
    amount: service?.amount ?? "",
    description: service?.description || "",
    active: service?.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Service name is required");
    if (Number(form.duration_min) <= 0) return toast.error("Duration must be greater than 0");
    if (Number(form.amount) < 0) return toast.error("Amount cannot be negative");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        duration_min: Number(form.duration_min),
        amount: Number(form.amount || 0),
        description: form.description.trim(),
        active: form.active,
      };
      if (isEdit) {
        await api.patch(`/services/${service.service_id}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/services", payload);
        toast.success("Service added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-nexa-accent/40 backdrop-blur-[2px] flex items-center justify-center p-3"
      onClick={onClose}
      data-testid="svc-modal"
    >
      <motion.form
        initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.3, ease: [0.2, 0.7, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="lux-card w-full max-w-[480px] p-4 relative"
      >
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-nexa-mute hover:text-nexa-ink" aria-label="Close">
          <X className="w-4 h-4" strokeWidth={1.7} />
        </button>

        <div className="lux-eyebrow">{isEdit ? "Edit" : "Add"}</div>
        <h2 className="text-[15px] font-semibold tracking-tight text-nexa-ink mt-1 mb-3">
          {isEdit ? service.name : "Add service"}
        </h2>

        <div className="space-y-2.5">
          <div>
            <label className="lux-label !text-[9px]">Service name <span className="text-red-500">*</span></label>
            <input data-testid="svc-form-name" className="lux-input !h-10 !px-3 !text-[12px]" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Haircut, Colour, Consultation…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="lux-label !text-[9px]">Duration (min) <span className="text-red-500">*</span></label>
              <input type="number" min="5" data-testid="svc-form-duration" className="lux-input !h-10 !px-3 !text-[12px]" value={form.duration_min} onChange={(e) => update("duration_min", e.target.value)} />
            </div>
            <div>
              <label className="lux-label !text-[9px]">Amount (₹) <span className="text-red-500">*</span></label>
              <input type="number" min="0" data-testid="svc-form-amount" className="lux-input !h-10 !px-3 !text-[12px]" value={form.amount} onChange={(e) => update("amount", e.target.value)} placeholder="500" />
            </div>
          </div>
          <div>
            <label className="lux-label !text-[9px]">Description</label>
            <textarea data-testid="svc-form-description" rows={2} className="lux-input !h-10 !px-3 !text-[12px]" value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Optional — short description shown to customers." />
          </div>
          <label className="flex items-center justify-between gap-3 rounded-lg border border-nexa-line px-3 py-2 cursor-pointer hover:bg-nexa-canvas/60 transition-colors">
            <div>
              <div className="text-[11px] text-nexa-ink font-medium">
                Active
              </div>

              <div className="text-[8.5px] text-nexa-mute mt-0.5">
                Inactive services are hidden from staff and appointment forms.
              </div>       
            </div>
            <button
              type="button" role="switch" aria-checked={form.active}
              onClick={() => update("active", !form.active)}
              data-testid="svc-form-active"
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.active ? "bg-nexa-accent" : "bg-nexa-line"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.active ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </label>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="lux-btn-secondary !px-4 !py-2 !text-[11px]">Cancel</button>
          <button type="submit" disabled={saving} className="lux-btn-accent !px-5 !py-2 !text-[11px]" data-testid="svc-form-submit">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add service"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}
